<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { DISPLAY_TYPE_TO_FIELD_TYPE } from './fieldTypeMap';
import { GROUP_CATALOGS } from './groupCatalogs';
import { defaultFormCountry } from './accountFormDefaults';

const props = defineProps({
  formType: { type: String, default: 'ticket' },
  // Non-empty mapped_attribute values already used by the form. Items mapped to
  // one of these are hidden so a specific ticket/contact field can't be reused.
  usedMappedAttributes: { type: Array, default: () => [] },
  // Custom-attribute bundles (CustomAttributeBundle) for this form's model,
  // fetched by the FormBuilder route. Each becomes a one-click palette card that
  // drops its member custom-attribute fields at once. Bundle creation lives in
  // Settings → Custom attributes, not here.
  bundles: { type: Array, default: () => [] },
});

const emit = defineEmits(['addField', 'addFields']);

const { t } = useI18n();

const { isCloudFeatureEnabled, accountId } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);

// Custom attribute definitions for the account — dispatched globally and also
// refreshed by the FormBuilder route on mount. Used to resolve bundle members
// and (for contact) to offer individual custom-attribute fields.
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const ticketAttributes = useMapGetter('attributes/getTicketAttributes');

const BUNDLE_ICON =
  'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4';

const USER_ICON =
  'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z';
const CONTACT_CUSTOM_ICON =
  'M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 9V4a1 1 0 011-1z';

const CONTACT_STATIC_ITEMS = [
  {
    kind: 'contact',
    field_type: 'text',
    mapped_attribute: 'contact.name',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_NAME',
    descKey: 'FORM_BUILDER.PALETTE.CONTACT_NAME_DESC',
    icon: USER_ICON,
  },
  {
    kind: 'contact',
    field_type: 'text',
    mapped_attribute: 'contact.last_name',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_LAST_NAME',
    descKey: 'FORM_BUILDER.PALETTE.CONTACT_LAST_NAME_DESC',
    icon: USER_ICON,
  },
  {
    kind: 'contact',
    field_type: 'email',
    mapped_attribute: 'contact.email',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_EMAIL',
    descKey: 'FORM_BUILDER.PALETTE.CONTACT_EMAIL_DESC',
    icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  },
  {
    kind: 'contact',
    field_type: 'phone',
    mapped_attribute: 'contact.phone_number',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_PHONE',
    descKey: 'FORM_BUILDER.PALETTE.CONTACT_PHONE_DESC',
    icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  },
  {
    kind: 'contact',
    field_type: 'text',
    mapped_attribute: 'contact.identifier',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_IDENTIFIER',
    descKey: 'FORM_BUILDER.PALETTE.CONTACT_IDENTIFIER_DESC',
    icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  },
];

// Social networks can be added two ways:
//  - as a single "group" (clicking adds one Social Media group whose `+ Add` menu —
//    in the builder and on the public form — lets you pick which networks to include);
//  - individually (one flat field per network, like any other mapped contact field).
// The group item's `mapped_attribute` token only hides it once a social group exists.
// Individual items derive from the same catalog so icons/mappings stay in one place.
const SOCIAL_GROUP_ITEM = {
  kind: 'group',
  catalog: 'social_media',
  mapped_attribute: 'group:social_media',
  labelKey: GROUP_CATALOGS.social_media.paletteLabelKey,
  descKey: GROUP_CATALOGS.social_media.paletteDescKey,
  icon: GROUP_CATALOGS.social_media.icon,
};

const CONTACT_SOCIAL_INDIVIDUAL_ITEMS = GROUP_CATALOGS.social_media.options.map(
  opt => ({
    kind: 'contact',
    field_type: opt.field_type || 'text',
    mapped_attribute: opt.mapped_attribute,
    labelKey: opt.labelKey,
    descKey: 'FORM_BUILDER.PALETTE.CONTACT_SOCIAL_DESC',
    icon: opt.icon,
  })
);

const CONTACT_SOCIAL_ITEMS = [
  SOCIAL_GROUP_ITEM,
  ...CONTACT_SOCIAL_INDIVIDUAL_ITEMS,
];

const ACCENT_STYLES = {
  teal: {
    card: 'border-teal-500/25 hover:bg-teal-500/5 hover:border-teal-500/40',
    iconBg: 'bg-teal-500/10',
    iconText: 'text-teal-500',
  },
  violet: {
    card: 'border-violet-500/25 hover:bg-violet-500/5 hover:border-violet-500/40',
    iconBg: 'bg-violet-500/10',
    iconText: 'text-violet-500',
  },
  none: {
    card: 'border-n-weak hover:bg-n-alpha-1 hover:border-n-strong',
    iconBg: 'bg-n-alpha-2',
    iconText: 'text-n-slate-11',
  },
};

const PALETTE_SECTIONS = [
  {
    key: 'ticket',
    accent: 'teal',
    labelKey: 'FORM_BUILDER.PALETTE.TICKET_FIELDS',
    items: [
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.client_name',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_CLIENT_NAME',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_CLIENT_NAME_DESC',
        icon: USER_ICON,
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.contract_number',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_CONTRACT',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_CONTRACT_DESC',
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      },
      {
        kind: 'ticket',
        field_type: 'select',
        mapped_attribute: 'ticket.priority',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_PRIORITY',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_PRIORITY_DESC',
        icon: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12',
        options: ['low', 'medium', 'high', 'urgent'],
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_street',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_STREET',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_STREET_DESC',
        icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_exterior_number',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_EXTERIOR_NUMBER',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_EXTERIOR_NUMBER_DESC',
        icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_interior_number',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_INTERIOR_NUMBER',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_INTERIOR_NUMBER_DESC',
        icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_colony',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_COLONY',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_COLONY_DESC',
        icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_postal_code',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_POSTAL',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_POSTAL_DESC',
        icon: 'M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11V4a4 4 0 118 0v7c0 3.517-1.009 6.799-2.753 9.571M15 11a3 3 0 11-6 0 3 3 0 016 0z',
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_city',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_CITY',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_CITY_DESC',
        icon: 'M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11h16V10',
      },
      {
        kind: 'ticket',
        field_type: 'text',
        mapped_attribute: 'ticket.address_state',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_STATE',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ADDRESS_STATE_DESC',
        icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064',
      },
      {
        kind: 'ticket',
        field_type: 'file',
        mapped_attribute: '',
        labelKey: 'FORM_BUILDER.PALETTE.TICKET_ATTACHMENTS',
        descKey: 'FORM_BUILDER.PALETTE.TICKET_ATTACHMENTS_DESC',
        icon: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
        max_files: 5,
        max_file_size: 10,
      },
    ],
  },
  {
    key: 'standard',
    labelKey: 'FORM_BUILDER.PALETTE.STANDARD',
    items: [
      {
        type: 'text',
        labelKey: 'FORM_BUILDER.PALETTE.TEXT',
        descKey: 'FORM_BUILDER.PALETTE.TEXT_DESC',
        icon: 'M4 6h16M4 12h16M4 18h7',
      },
      {
        type: 'textarea',
        labelKey: 'FORM_BUILDER.PALETTE.TEXTAREA',
        descKey: 'FORM_BUILDER.PALETTE.TEXTAREA_DESC',
        icon: 'M4 6h16M4 10h16M4 14h16M4 18h10',
      },
      {
        type: 'email',
        labelKey: 'FORM_BUILDER.PALETTE.EMAIL',
        descKey: 'FORM_BUILDER.PALETTE.EMAIL_DESC',
        icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      },
      {
        type: 'phone',
        labelKey: 'FORM_BUILDER.PALETTE.PHONE',
        descKey: 'FORM_BUILDER.PALETTE.PHONE_DESC',
        icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
      },
      {
        type: 'number',
        labelKey: 'FORM_BUILDER.PALETTE.NUMBER',
        descKey: 'FORM_BUILDER.PALETTE.NUMBER_DESC',
        icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
      },
      {
        type: 'address',
        labelKey: 'FORM_BUILDER.PALETTE.ADDRESS',
        descKey: 'FORM_BUILDER.PALETTE.ADDRESS_DESC',
        icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
      },
    ],
  },
  {
    key: 'choice',
    labelKey: 'FORM_BUILDER.PALETTE.CHOICE',
    items: [
      {
        type: 'select',
        labelKey: 'FORM_BUILDER.PALETTE.DROPDOWN',
        descKey: 'FORM_BUILDER.PALETTE.DROPDOWN_DESC',
        icon: 'M8 9l4-4 4 4m0 6l-4 4-4-4',
      },
      {
        type: 'checkbox',
        labelKey: 'FORM_BUILDER.PALETTE.CHECKBOX',
        descKey: 'FORM_BUILDER.PALETTE.CHECKBOX_DESC',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      },
      {
        type: 'date',
        labelKey: 'FORM_BUILDER.PALETTE.DATE',
        descKey: 'FORM_BUILDER.PALETTE.DATE_DESC',
        icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      },
    ],
  },
  {
    key: 'media',
    labelKey: 'FORM_BUILDER.PALETTE.MEDIA',
    items: [
      {
        type: 'file',
        labelKey: 'FORM_BUILDER.PALETTE.FILE_UPLOAD',
        descKey: 'FORM_BUILDER.PALETTE.FILE_UPLOAD_DESC',
        icon: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
      },
      {
        type: 'documents',
        labelKey: 'FORM_BUILDER.PALETTE.DOCUMENTS',
        descKey: 'FORM_BUILDER.PALETTE.DOCUMENTS_DESC',
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      },
    ],
  },
];

// One palette item per account contact custom attribute, pre-mapped to contact.custom.<key>.
const contactCustomItems = computed(() =>
  (contactAttributes.value || []).map(attr => ({
    kind: 'contact',
    field_type: DISPLAY_TYPE_TO_FIELD_TYPE[attr.attributeDisplayType] || 'text',
    mapped_attribute: `contact.custom.${attr.attributeKey}`,
    label: attr.attributeDisplayName,
    description: attr.attributeKey,
    options: Array.isArray(attr.attributeValues)
      ? [...attr.attributeValues]
      : [],
    icon: CONTACT_CUSTOM_ICON,
  }))
);

// Model prefix + matching custom-attribute getter for the current form type.
const bundleModel = computed(() =>
  props.formType === 'contact'
    ? { prefix: 'contact', attrs: contactAttributes }
    : { prefix: 'ticket', attrs: ticketAttributes }
);

// Resolve a bundle's member attribute keys into form-field definitions using the
// account's custom-attribute definitions (type, label, list options). Members
// whose attribute no longer exists are skipped.
function bundleMemberFields(bundle) {
  const { prefix, attrs } = bundleModel.value;
  const byKey = new Map((attrs.value || []).map(a => [a.attributeKey, a]));
  return (Array.isArray(bundle.fields) ? [...bundle.fields] : [])
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(field => {
      const attr = byKey.get(field.attribute_key);
      if (!attr) return null;
      return {
        field_type:
          DISPLAY_TYPE_TO_FIELD_TYPE[attr.attributeDisplayType] || 'text',
        label: attr.attributeDisplayName,
        placeholder: '',
        required: field.required !== false,
        options: Array.isArray(attr.attributeValues)
          ? [...attr.attributeValues]
          : [],
        mapped_attribute: `${prefix}.custom.${attr.attributeKey}`,
        validation_regex: '',
      };
    })
    .filter(Boolean);
}

// One palette card per custom-attribute bundle. Members are pre-resolved so the
// card can hide once they're all on the form and drop the rest in one click.
const bundleItems = computed(() =>
  (props.bundles || []).map(bundle => ({
    kind: 'bundle',
    key: `bundle:${bundle.id}`,
    label: bundle.bundle_name,
    description: bundle.description || t('FORM_BUILDER.PALETTE.BUNDLE_DESC'),
    icon: BUNDLE_ICON,
    fields: bundleMemberFields(bundle),
  }))
);

// A mapped item whose mapped_attribute is already on the form is hidden so the
// same specific ticket/contact field can't be added twice. Items with an empty
// mapped_attribute (generic + the attachments file field) are always kept.
const usedAttributes = computed(() => new Set(props.usedMappedAttributes));

// Bundle members already on the form are dropped; the bundle adds only the rest.
function addableBundleFields(item) {
  return (item.fields || []).filter(
    field =>
      !field.mapped_attribute ||
      !usedAttributes.value.has(field.mapped_attribute)
  );
}

function isItemAvailable(item) {
  // A bundle hides once all its members are already on the form.
  if (item.kind === 'bundle') {
    return addableBundleFields(item).length > 0;
  }
  return !item.mapped_attribute || !usedAttributes.value.has(item.mapped_attribute);
}

// Contact section is inserted right after the Ticket section.
const sections = computed(() => {
  const [ticketSection, standardSection, choiceSection, mediaSection] =
    PALETTE_SECTIONS;
  const contactSection = {
    key: 'contact',
    accent: 'violet',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_FIELDS',
    items: [...CONTACT_STATIC_ITEMS, ...contactCustomItems.value],
  };
  const contactSocialSection = {
    key: 'contactSocial',
    accent: 'violet',
    labelKey: 'FORM_BUILDER.PALETTE.CONTACT_SOCIAL_MEDIA',
    items: CONTACT_SOCIAL_ITEMS,
  };
  // Custom-attribute bundles defined in Settings → Custom attributes. Shown first
  // as one-click cards; empty when none are defined (filtered out below).
  const bundleSection = {
    key: 'bundles',
    accent: 'teal',
    labelKey: 'FORM_BUILDER.PALETTE.BUNDLES',
    items: bundleItems.value,
  };
  // Contact forms have no ticket fields and no file uploads.
  const baseSections =
    props.formType === 'contact'
      ? [
          bundleSection,
          contactSection,
          contactSocialSection,
          standardSection,
          choiceSection,
        ]
      : [
          bundleSection,
          // Drop the contract-number field when the contracts feature is disabled.
          isContractsEnabled.value
            ? ticketSection
            : {
                ...ticketSection,
                items: ticketSection.items.filter(
                  item => item.mapped_attribute !== 'ticket.contract_number'
                ),
              },
          contactSection,
          contactSocialSection,
          standardSection,
          choiceSection,
          mediaSection,
        ];

  // Filter out already-used mapped items, then drop any section left empty.
  return baseSections
    .map(section => ({
      ...section,
      items: section.items.filter(isItemAvailable),
    }))
    .filter(section => section.items.length > 0);
});

function accentStyles(section) {
  return ACCENT_STYLES[section.accent] || ACCENT_STYLES.none;
}

function itemLabel(item) {
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return item.label || t(item.labelKey);
}

function itemDescription(item) {
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return item.description || t(item.descKey);
}

// A group palette item builds an empty group field; members are added later via
// the group card's `+ Add` menu. The persisted field has no mapped_attribute (its
// members carry the mappings) — only the palette item keeps the hiding token.
function buildGroupFieldDefaults(item) {
  const catalog = GROUP_CATALOGS[item.catalog];
  return {
    field_type: 'group',
    catalog: item.catalog,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(catalog.defaultLabelKey),
    members: [],
    mapped_attribute: '',
  };
}

// Ticket and contact palette items carry a preset field definition (mapped_attribute, etc.).
function buildMappedFieldDefaults(item) {
  const defaults = {
    field_type: item.field_type,
    label: itemLabel(item),
    placeholder: '',
    required: false,
    options: item.options ? [...item.options] : [],
    mapped_attribute: item.mapped_attribute || '',
    validation_regex: '',
  };
  if (item.field_type === 'file') {
    defaults.max_files = item.max_files ?? 5;
    defaults.max_file_size = item.max_file_size ?? 10;
  }
  return defaults;
}

function fieldDefaultsForItem(item) {
  return item.kind === 'group'
    ? buildGroupFieldDefaults(item)
    : buildMappedFieldDefaults(item);
}

function handleDragStart(e, item) {
  if (item.kind === 'bundle') {
    e.dataTransfer.setData(
      'fields-bundle',
      JSON.stringify(addableBundleFields(item))
    );
    e.dataTransfer.effectAllowed = 'copy';
    return;
  }
  if (item.kind) {
    e.dataTransfer.setData('field-data', JSON.stringify(fieldDefaultsForItem(item)));
    e.dataTransfer.effectAllowed = 'copy';
    return;
  }
  e.dataTransfer.setData('field-type', item.type);
  e.dataTransfer.effectAllowed = 'copy';
}

function handleClick(item) {
  if (item.kind === 'bundle') {
    emit('addFields', addableBundleFields(item));
    return;
  }
  if (item.kind) {
    emit('addField', fieldDefaultsForItem(item));
    return;
  }

  emit('addField', buildGenericFieldDefaults(item.type));
}

// Defaults for a generic (non-mapped) palette item. A documents field carries its own
// additional-documents list and opts into the subcategory templates by default.
function buildGenericFieldDefaults(type) {
  return {
    field_type: type,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(`FORM_BUILDER.PALETTE.${type.toUpperCase()}`),
    placeholder: '',
    required: false,
    options: type === 'select' ? ['Option 1', 'Option 2'] : [],
    mapped_attribute: '',
    validation_regex: '',
    max_files: type === 'file' ? 5 : undefined,
    max_file_size: type === 'file' ? 10 : undefined,
    documents: type === 'documents' ? [] : undefined,
    include_subcategory_templates: type === 'documents' ? true : undefined,
    // Bias new address fields to the account's default country (e.g. Venezuela for
    // the Reencuentro account) so search + map start in the right place.
    country:
      type === 'address' ? defaultFormCountry(accountId.value) : undefined,
  };
}
</script>

<template>
  <div class="w-52 border-l border-n-weak bg-n-slate-1 shrink-0 flex flex-col">
    <div class="p-3 border-b border-n-weak">
      <span
        class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
      >
        {{ t('FORM_BUILDER.PALETTE.TITLE') }}
      </span>
    </div>
    <div class="flex-1 overflow-y-auto p-2 space-y-1">
      <template v-for="section in sections" :key="section.key">
        <div
          class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider px-2 py-1.5"
          :class="{ 'mt-2': section.key !== 'ticket' }"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
          {{ t(section.labelKey) }}
        </div>
        <div
          v-for="item in section.items"
          :key="item.key || item.mapped_attribute || item.labelKey"
          class="rounded-lg p-2.5 border cursor-grab flex items-center gap-2.5 transition-all"
          :class="accentStyles(section).card"
          draggable="true"
          @dragstart="handleDragStart($event, item)"
          @click="handleClick(item)"
        >
          <div
            class="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            :class="accentStyles(section).iconBg"
          >
            <svg
              class="w-3.5 h-3.5"
              :class="accentStyles(section).iconText"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path :d="item.icon" />
            </svg>
          </div>
          <div>
            <div class="flex items-center gap-1.5">
              <span class="text-xs font-medium text-n-slate-12">
                {{ itemLabel(item) }}
              </span>
              <span
                v-if="item.kind === 'group'"
                class="text-[9px] font-semibold uppercase tracking-wider px-1 py-px rounded text-violet-400 bg-violet-400/10 shrink-0"
              >
                {{ t('FORM_BUILDER.GROUP.BADGE') }}
              </span>
              <span
                v-else-if="item.type === 'documents'"
                class="text-[9px] font-semibold uppercase tracking-wider px-1 py-px rounded text-teal-500 bg-teal-500/10 shrink-0"
              >
                {{ t('FORM_BUILDER.PALETTE.REVIEWABLE_BADGE') }}
              </span>
            </div>
            <div class="text-[10px] text-n-slate-10">
              {{ itemDescription(item) }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
